import React, { useState, useMemo } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';

const CPTCodesField = ({
  dataPoint,
  index,
  handleFieldChange,
  handleCheckboxChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // CPT codes organized by category with full definitions
  const cptCodesData = {
    'OCCUPATIONAL THERAPY EVALUATIONS': {
      97165: 'Occupational therapy evaluation, low complexity',
      97166: 'Occupational therapy evaluation, moderate complexity',
      97167: 'Occupational therapy evaluation, high complexity',
      97168:
        'Occupational therapy re-evaluation (Report 97168 for performance of a reevaluation that is based on an established and ongoing plan of care)',
    },
    'THERAPEUTIC PROCEDURES': {
      97530:
        'Therapeutic activities, direct (one-on-one) patient contact (use of dynamic activities to improve functional performance), each 15 minutes',
      97533:
        'Sensory integrative techniques to enhance sensory processing and promote adaptive responses to environmental demands, direct (one-on-one) patient contact, each 15 minutes',
      97535:
        'Self-care/home management training (e.g., activities of daily living [ADLs] and compensatory training, meal preparation, safety procedures, and instructions in use of assistive technology devices/adaptive equipment), direct one-on-one contact, each 15 minutes',
      97537:
        'Community/work reintegration training (e.g., shopping, transportation, money management, avocational activities and/or work environment/modification analysis, work task analysis, use of assistive technology device/adaptive equipment), direct one-on-one contact, each 15 minutes',
      97150:
        'Therapeutic procedure(s), group (2 or more), untimed (Report for each member of the group) (Group therapy procedures involve constant attendance by the qualified health care professional but do not require one-on-one patient contact by the same health care professional)',
      97112:
        'Neuromuscular reeducation of movement, balance, coordination, kinesthetic sense, posture, and/or proprioception for sitting and/or standing activities',
      97116: 'Gait training (includes stair climbing)',
      97124:
        'Massage, including effleurage, petrissage, and/or tapotement (stroking, compression, percussion)',
      97542:
        'Wheelchair management (e.g., assessment, fitting, training), each 15 minutes',
      97550:
        "Caregiver training in strategies and techniques to facilitate the patient's functional performance in the home or community (eg, activities of daily living [ADLs], instrumental ADLs [IADLs], transfers, mobility, communication, swallowing, feeding, problem solving, safety practices) (without the patient present), face to face; initial 30 minutes",
    },
    'TEST AND MEASUREMENTS': {
      97750:
        'Physical performance test or measurement (e.g., musculoskeletal, functional capacity), with written report, each 15 minutes',
      97755:
        'Assistive technology assessment (e.g., to restore, augment, or compensate for existing function, optimize functional tasks, and/or maximize environmental accessibility), direct one-on-one contact, with written report, each 15 minutes',
    },
  };

  // Create a flat list of codes with their categories and descriptions
  const allCodesWithMetadata = useMemo(() => {
    const codes = [];
    Object.entries(cptCodesData).forEach(([category, codesInCategory]) => {
      Object.entries(codesInCategory).forEach(([code, description]) => {
        codes.push({
          code: code.toString(),
          description,
          category,
        });
      });
    });
    return codes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return allCodesWithMetadata;
    return allCodesWithMetadata.filter(
      (item) =>
        item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, allCodesWithMetadata]);

  // Group filtered options by category
  // const groupedOptions = useMemo(() => {
  //   const grouped = {};
  //   filteredOptions.forEach((item) => {
  //     if (!grouped[item.category]) {
  //       grouped[item.category] = [];
  //     }
  //     grouped[item.category].push(item);
  //   });
  //   return grouped;
  // }, [filteredOptions]);

  const selectedCode = dataPoint.cptCodes || '';
  // const selectedItem = allCodesWithMetadata.find(
  //   (item) => item.code === selectedCode
  // );

  return (
    <Box sx={{ gap: 1 }}>
      <FormControl sx={{ width: '100%', maxWidth: '500px' }}>
        <Select
          value={selectedCode}
          onChange={(e) => {
            const newValue = e.target.value;
            try {
              handleFieldChange(index, 'cptCodes', newValue);

              // Automatically check the "Add CPT Code" checkbox when a code is selected
              if (
                newValue &&
                !dataPoint.selectedOptions.includes('Add CPT Code')
              ) {
                const checkboxEvent = {
                  target: {
                    value: 'Add CPT Code',
                    checked: true,
                  },
                };
                handleCheckboxChange(index, checkboxEvent);
              }
            } catch (error) {
              console.error('Error in handleFieldChange:', error);
            }
          }}
          displayEmpty
          size='small'
          onOpen={() => setSearchTerm('')}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 500,
                width: '100%',
                maxWidth: '600px',
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            disableAutoFocusItem: false,
            autoFocus: false,
          }}
          sx={{
            backgroundColor: 'var(--clr-mindly-white)',
            '& .MuiOutlinedInput-root': {
              height: '40px',
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                fontSize: '14px',
              },
            },
          }}
          renderValue={(value) => {
            if (!value) return 'Select CPT Code';
            const item = allCodesWithMetadata.find(
              (item) => item.code === value
            );
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                  {value}
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  {item?.category}
                </Typography>
              </Box>
            );
          }}
        >
          {/* Search Box - moved to top */}
          <Box
            sx={{
              p: 2,
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              zIndex: 1000,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
          >
            <TextField
              size='small'
              placeholder='Search CPT code, description, or category...'
              fullWidth
              value={searchTerm}
              onChange={(e) => {
                e.stopPropagation();
                setSearchTerm(e.target.value);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search
                      fontSize='small'
                      sx={{ color: 'var(--clr-theme-purple)' }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'var(--clr-theme-purple)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--clr-theme-purple)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--clr-theme-purple)',
                  },
                },
              }}
            />
          </Box>

          {/* Flat list of all options */}
          {filteredOptions.map((item) => (
            <MenuItem
              key={item.code}
              value={item.code}
              sx={{
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                minHeight: 'auto',
                py: 2,
                px: 2,
                borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'var(--clr-theme-purple)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'var(--clr-theme-purple)',
                  },
                },
              }}
            >
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                    }}
                  >
                    {item.code}
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{
                      color: 'var(--clr-theme-purple)',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {item.category}
                  </Typography>
                </Box>
                <Typography
                  variant='caption'
                  sx={{
                    display: 'block',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    lineHeight: 1.4,
                    width: '100%',
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                  }}
                >
                  {item.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}

          {/* No Results */}
          {filteredOptions.length === 0 && searchTerm && (
            <Box
              sx={{ p: 2, textAlign: 'center' }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                No CPT codes found matching "{searchTerm}"
              </Typography>
            </Box>
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default CPTCodesField;
